import React, { useEffect, useRef, useState } from 'react';

import { hojeIso } from '../formatacao';
import '../../../components/modal/add-car/AddCarModal.css';
import '../../../shared/ui/modal-close-button.css';
import '../../admin-sistema/pages/css/adminSistema.css';
import '../pages/OficinaExterna.css';

interface Props {
  placa: string;
  onCancelar: () => void;
  onConfirmar: (dados: { saidaEm: string; motivo?: string }) => Promise<void>;
}

/**
 * Registro da saída do veículo da oficina externa (at-114).
 *
 * Modal próprio em vez do `ConfirmModal` compartilhado porque a saída pede DOIS campos (a data,
 * que pode ser anterior a hoje, e o motivo) — e aquele componente recebe só texto.
 */
const SaidaModal: React.FC<Props> = ({ placa, onCancelar, onConfirmar }) => {
  const [saidaEm, setSaidaEm] = useState(hojeIso());
  const [motivo, setMotivo] = useState('');
  const [salvando, setSalvando] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const cliqueFora = (evento: MouseEvent) => {
      if (!modalRef.current?.contains(evento.target as Node)) onCancelar();
    };
    const escape = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') onCancelar();
    };
    document.addEventListener('mousedown', cliqueFora);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', cliqueFora);
      document.removeEventListener('keydown', escape);
    };
  }, [onCancelar]);

  return (
    <div className="add-car-modal-overlay">
      <div className="modal-content oe-modal" ref={modalRef} role="dialog" aria-modal="true">
        <div className="oe-modal-header">
          <h2>Registrar saída — {placa}</h2>
          <button type="button" className="close-button" onClick={onCancelar} aria-label="Fechar">
            &times;
          </button>
        </div>

        <div className="oe-modal-body">
          <p className="oe-hint">
            O veículo sai da lista desta oficina. As visitas já registradas continuam no histórico,
            e este registro é enviado quando houver rede.
          </p>

          <div className="oe-form">
            <label className="oe-campo">
              <span className="oe-campo-obrigatorio">Data da saída</span>
              <input
                type="date"
                max={hojeIso()}
                value={saidaEm}
                onChange={(e) => setSaidaEm(e.target.value)}
              />
            </label>

            <label className="oe-campo">
              <span>Motivo (opcional)</span>
              <input
                type="text"
                maxLength={200}
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Ex: entregue ao associado"
              />
            </label>

            <div className="oe-form-acoes">
              <button
                type="button"
                className="admin-btn admin-btn-primary"
                disabled={salvando || !saidaEm}
                onClick={async () => {
                  setSalvando(true);
                  try {
                    await onConfirmar({
                      // Meio-dia local: a data é "o dia da saída", e a hora não é informada por
                      // ninguém. Meia-noite viraria o dia anterior em fusos a oeste de UTC.
                      saidaEm: new Date(`${saidaEm}T12:00:00`).toISOString(),
                      motivo: motivo.trim() || undefined,
                    });
                  } finally {
                    setSalvando(false);
                  }
                }}
              >
                {salvando ? 'Gravando...' : 'Registrar saída'}
              </button>
              <button
                type="button"
                className="admin-btn admin-btn-secondary"
                disabled={salvando}
                onClick={onCancelar}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SaidaModal;
